import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { faDownload, faCheck, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import swal from "sweetalert";
import { ENV } from "../../util/Env";

const ZipDownload = () => {
  const { fileId, email } = useParams();
  const [loading, setLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const hasDownloadStarted = useRef(false);
  const navigate = useNavigate();


  useEffect(() => {
    if (fileId && email && !hasDownloadStarted.current) {
      hasDownloadStarted.current = true;
      setDownloadStatus('Initiating download...');
      downloadZip();
    }
  }, [fileId, email]);


  const downloadZip = async () => {
    const url = ENV.API_URL + `files/files/temp/download`;
    try {
      setLoading(true);
      setDownloadStatus('Connecting...');
      setDownloadProgress(0);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: ``,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: fileId,
          email: email,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setDownloadStatus('Downloading...');
      setDownloadProgress(5);

      const contentLength = response.headers.get('Content-Length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : null;

      const contentDisposition = response.headers.get('Content-Disposition');
      let downloadFileName = `Event_Details_${new Date().toISOString().slice(0, 10)}.zip`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          downloadFileName = fileNameMatch[1].replace(/['"]/g, '');
        } else {
          const fileNameStarMatch = contentDisposition.match(/filename\*=(?:UTF-8'')?(.+)/);
          if (fileNameStarMatch && fileNameStarMatch[1]) {
            downloadFileName = decodeURIComponent(fileNameStarMatch[1]);
          }
        }
      }

      setFileName(downloadFileName);
      const reader = response.body.getReader();
      const chunks = [];
      let receivedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        chunks.push(value);
        receivedBytes += value.length;

        if (totalBytes) {
          const progress = Math.round((receivedBytes / totalBytes) * 90); // Max 90% during download
          setDownloadProgress(Math.min(progress + 5, 90)); // +5 for initial connection

          if (progress < 30) {
            setDownloadStatus('Downloading...');
          } else if (progress < 60) {
            setDownloadStatus('Processing...');
          } else if (progress < 90) {
            setDownloadStatus('Finalizing...');
          }
        } else {
          const timeBasedProgress = Math.min(receivedBytes / 1000000 * 50, 80);
          setDownloadProgress(Math.min(timeBasedProgress + 5, 85));
          setDownloadStatus('Downloading...');
        }
      }

      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combinedArray = new Uint8Array(totalLength);
      let offset = 0;

      for (const chunk of chunks) {
        combinedArray.set(chunk, offset);
        offset += chunk.length;
      }

      setDownloadStatus('Creating file...');
      setDownloadProgress(95);
      const blob = new Blob([combinedArray], { type: 'application/zip' });

      setDownloadStatus('Preparing download...');
      setDownloadProgress(98);

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      setDownloadStatus('Completed');
      setDownloadProgress(100);
      setDownloadComplete(true);

      setTimeout(() => {
        swal({
          title: "Success!",
          text: "File downloaded successfully",
          icon: "success",
          button: "OK",
          dangerMode: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        }).then(() => {
          navigate('/');
        });
      }, 1000);

    } catch (error) {
      console.error("Download error:", error);
      setDownloadProgress(0);
      setDownloadError(true);

      // Handle errors
      let errorMessage = "An unexpected error occurred";

      if (error.message.includes('HTTP error')) {
        if (error.message.includes('404')) {
          errorMessage = "File not found on server";
          setDownloadStatus('File not found');
        } else if (error.message.includes('403')) {
          errorMessage = "Access denied to download file";
          setDownloadStatus('Access denied');
        } else if (error.message.includes('500')) {
          errorMessage = "Server error occurred";
          setDownloadStatus('Server error');
        } else {
          errorMessage = `Server responded with error: ${error.message}`;
          setDownloadStatus('Download failed');
        }
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = "Network error - please check your connection";
        setDownloadStatus('Network error');
      } else {
        errorMessage = error.message;
        setDownloadStatus('Download failed');
      }

      swal({
        title: "Download Failed!",
        text: errorMessage,
        icon: "error",
        button: "OK",
        dangerMode: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      }).then(() => {
        navigate('/');
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="download_wrapper">
        <div className="download_container">
          <div className="download_card">
            <div className="progress_circle">
              <svg className="progress_ring" viewBox="0 0 200 200" width="200" height="200">
                <circle
                  className="progress_ring_bg"
                  stroke="#e6e6e6"
                  strokeWidth="8"
                  fill="transparent"
                  r="90"
                  cx="100"
                  cy="100"
                />
                <circle
                  className="progress_ring_fill"
                  stroke={downloadError ? "#ff4757" : downloadComplete ? "#2ed573" : "rgba(var(--btn-bg-color))"}
                  strokeWidth="8"
                  fill="transparent"
                  r="90"
                  cx="100"
                  cy="100"
                  strokeDasharray={`${2 * Math.PI * 90}`}
                  strokeDashoffset={`${2 * Math.PI * 90 * (1 - downloadProgress / 100)}`}
                  style={{
                    transition: 'stroke-dashoffset 0.5s ease-in-out',
                    transform: 'rotate(-90deg)',
                    transformOrigin: '50% 50%'
                  }}
                />
              </svg>

              <div className="center_content">
                <div className="download_icon">
                  {downloadError ? (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="icon error" />
                  ) : downloadComplete ? (
                    <FontAwesomeIcon icon={faCheck} className="icon success" />
                  ) : (
                    <FontAwesomeIcon
                      icon={faDownload}
                      className={`icon downloading ${loading ? 'animate' : ''}`}
                    />
                  )}
                </div>

                {!downloadError && (
                  <div className="percentage_text">
                    {downloadComplete ? 'Done' : `${downloadProgress}%`}
                  </div>
                )}

                {downloadError && (
                  <div className="error_text">
                    Failed
                  </div>
                )}
              </div>
            </div>

            <div className="status_container">
              <h2 className="download_title">File Download</h2>
              {/* {fileName && (
                <p className="file_name">
                  <strong>File:</strong> {fileName}
                </p>
              )} */}
              <p className="status_message">
                {downloadStatus || 'Preparing...'}
              </p>
              {!downloadError && !downloadComplete && (
                <div className="loading_dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </React.Fragment>
  );
};

export default ZipDownload;
